import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ratchet_token");
    if (!token) {
      setUser(null); setOrg(null); setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user); setOrg(data.org);
    } catch {
      localStorage.removeItem("ratchet_token");
      setUser(null); setOrg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Persiste a sessão completa (token + user + org) devolvida após o TOTP.
  const applySession = (data) => {
    localStorage.setItem("ratchet_token", data.token);
    setUser(data.user); setOrg(data.org);
    return data;
  };

  // Passo 1 do login: senha. Pode devolver {totp_required|totp_setup_required, preauth_token}.
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) applySession(data);       // caso TOTP_REQUIRED=false
    return data;
  };

  // Signup etapa 1: pede o código por email (não cria conta ainda).
  const requestSignup = async (payload) => {
    const { data } = await api.post("/auth/signup/request-code", payload);
    return data;                              // {pending: true}
  };

  // Signup etapa 2: valida o código do email → cria conta → exige setup de TOTP.
  const verifyEmailOtp = async (email, code) => {
    const { data } = await api.post("/auth/signup/verify", { email, code });
    return data;                              // {totp_setup_required, preauth_token, email}
  };

  const resendEmailOtp = async (email) => {
    const { data } = await api.post("/auth/signup/resend-code", { email });
    return data;
  };

  // Bearer do preauth token (curto) — usado nos passos de TOTP antes de existir sessão.
  const _pre = (preauth) => ({ headers: { Authorization: `Bearer ${preauth}` } });

  // Provisiona um segredo TOTP + QR pro authenticator.
  const totpSetup = async (preauth) => {
    const { data } = await api.post("/auth/totp/setup", {}, _pre(preauth));
    return data;                              // {otpauth_uri, qr, secret}
  };

  // Confirma o código do app → ativa 2FA → devolve e aplica a sessão.
  const totpConfirm = async (preauth, code) => {
    const { data } = await api.post("/auth/totp/setup/confirm", { code }, _pre(preauth));
    return applySession(data);
  };

  // Passo 2 do login (conta já com 2FA): valida o código do app → aplica sessão.
  const loginTotp = async (preauth, code) => {
    const { data } = await api.post("/auth/login/totp", { code }, _pre(preauth));
    return applySession(data);
  };

  // Painel interno (owners@): step-up por email OTP a cada sessão.
  const panelRequestOtp = async () => {
    const { data } = await api.post("/admin/panel/request-otp");
    return data;
  };
  const panelVerifyOtp = async (code) => {
    const { data } = await api.post("/admin/panel/verify-otp", { code });
    sessionStorage.setItem("ratchet_panel_token", data.panel_token);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("ratchet_token");
    sessionStorage.removeItem("ratchet_panel_token");
    setUser(null); setOrg(null);
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{
      user, org, loading, refresh, logout,
      login, requestSignup, verifyEmailOtp, resendEmailOtp,
      totpSetup, totpConfirm, loginTotp,
      panelRequestOtp, panelVerifyOtp,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
